import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import { Modal, QRCode, message } from "antd";
import Logo from "../../assests/rhino.webp";
import { PORTAL_CERTIFICATE_SUBMIT_API } from "../../apis/apis";
import { certificateVerifyUrl, downloadCertificate, formatDate, getErr, usePortalExams } from "../../components/portalUtils";
import { ErrorBox, Loader } from "../../components/PortalBits";

/* Same rules as the prototype (and enforced again on the server). */
const ID_VALIDATORS = {
  "Aadhar Card": {
    hint: "Enter the 12-digit number exactly as printed, e.g. 234567890123",
    placeholder: "e.g. 234567890123",
    maxLength: 12,
    normalize: (v) => v.replace(/[\s-]/g, ""),
    test: (v) => /^\d{12}$/.test(v),
    error: "Aadhar number must be exactly 12 digits (numbers only).",
  },
  "PAN Card": {
    hint: "10 characters: 5 letters, 4 digits, 1 letter, e.g. ABCDE1234F",
    placeholder: "e.g. ABCDE1234F",
    maxLength: 10,
    normalize: (v) => v.toUpperCase().replace(/\s/g, ""),
    test: (v) => /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v),
    error: "PAN must be 10 characters in the format AAAAA9999A (5 letters, 4 digits, 1 letter).",
  },
  "Voter ID": {
    hint: "10 characters: 3 letters followed by 7 digits, e.g. ABC1234567",
    placeholder: "e.g. ABC1234567",
    maxLength: 10,
    normalize: (v) => v.toUpperCase().replace(/\s/g, ""),
    test: (v) => /^[A-Z]{3}[0-9]{7}$/.test(v),
    error: "Voter ID (EPIC) must be 3 letters followed by 7 digits.",
  },
  "Driving License": {
    hint: "8 to 16 letters and digits, exactly as printed on your licence",
    placeholder: "e.g. MH1420110012345",
    maxLength: 16,
    normalize: (v) => v.toUpperCase().replace(/[\s-]/g, ""),
    test: (v) => /^[A-Z0-9]{8,16}$/.test(v),
    error: "Enter a valid driving licence number (8 to 16 letters and digits).",
  },
};
const MAX_BYTES = 5 * 1024 * 1024;
const Req = () => <span className="req-star" aria-hidden="true">*</span>;
const OK_EXT = /\.(pdf|jpe?g|png)$/i;

function Certification() {
  const location = useLocation();
  const { user } = useOutletContext() || {};
  const { loading, error, exams, reload } = usePortalExams();
  const fileRef = useRef(null);

  const attempted = useMemo(
    () => exams.filter((e) => e.attempts.some((a) => a.state !== "in-progress") && e.certificate_enabled),
    [exams]
  );
  const passed = useMemo(() => attempted.filter((e) => e.attempts.some((a) => a.state === "passed")), [attempted]);

  const [activeCode, setActiveCode] = useState(location.state?.exam_code || null);
  const [fullName, setFullName] = useState("");
  const [idType, setIdType] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [invalid, setInvalid] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverErr, setServerErr] = useState(null);

  useEffect(() => {
    if (user?.name && !fullName) setFullName(user.name);
  }, [user, fullName]);

  useEffect(() => {
    if (!passed.length) return;
    if (!activeCode || !passed.some((e) => e.exam_code === activeCode)) setActiveCode(passed[0].exam_code);
  }, [passed, activeCode]);

  const exam = passed.find((e) => e.exam_code === activeCode);
  const cert = exam?.certificate || { status: "not_submitted" };
  const rule = ID_VALIDATORS[idType];
  const normalized = rule ? rule.normalize(idNumber) : "";
  const idValid = rule && normalized ? rule.test(normalized) : null;

  const resetForm = () => {
    setIdType("");
    setIdNumber("");
    setFile(null);
    setConsent(false);
    setInvalid({});
    setServerErr(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!OK_EXT.test(f.name)) {
      message.error("Only PDF, JPG or PNG files are allowed.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      message.error("File is larger than 5MB.");
      e.target.value = "";
      return;
    }
    setFile(f);
    setInvalid((s) => ({ ...s, file: false }));
  };

  const submit = async () => {
    setServerErr(null);
    const bad = {
      name: !fullName.trim(),
      type: !idType,
      number: !idNumber.trim() || (rule && !rule.test(normalized)),
      file: !file,
      consent: !consent,
    };
    setInvalid(bad);
    const missing = [];
    if (bad.name) missing.push("Full name");
    if (bad.type) missing.push("Select government ID");
    if (!idNumber.trim()) missing.push("Government ID number");
    if (bad.file) missing.push("Upload government ID");
    if (bad.consent) missing.push("Consent (read and accept)");
    if (missing.length) {
      setServerErr(`Mandatory field not filled: ${missing.join(", ")}`);
      Modal.warning({
        title: "Mandatory field not filled",
        content: (
          <ul style={{ paddingLeft: 18, margin: "8px 0 0" }}>
            {missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        ),
      });
      return;
    }
    if (bad.number) {
      setServerErr(rule.error);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("exam_code", exam.exam_code);
      fd.append("full_name", fullName.trim());
      fd.append("id_type", idType);
      fd.append("id_number", normalized);
      fd.append("id_file", file);
      fd.append("consent", "true");
      const res = await PORTAL_CERTIFICATE_SUBMIT_API(fd);
      if (res?.status === false) throw Object.assign(new Error(res.message), { response: { data: res } });
      message.success("Submitted for verification");
      resetForm();
      await reload(true);
    } catch (err) {
      setServerErr(getErr(err, "Unable to submit your details."));
    } finally {
      setSubmitting(false);
    }
  };

  const showForm = exam && ["not_submitted", "rejected"].includes(cert.status);

  return (
    <div className="panel">
      <h2>Certificate verification</h2>
      <div className="sub">
        {passed.length ? "Select an exam below, then submit your details to verify eligibility." : "Available once you pass an exam."}
      </div>

      {loading && <Loader />}
      {!loading && error && <ErrorBox message={error} onRetry={() => reload()} />}

      {!loading && !error && !passed.length && (
        <div className="cert-gate">You'll be able to submit your details for certificate verification after you pass an exam.</div>
      )}

      {!loading && !error && passed.length > 0 && (
        <>
          <div className="cert-form">
            <div className="field full">
              <label htmlFor="certExamSelect">Select examination</label>
              <select id="certExamSelect" value={activeCode || ""} onChange={(e) => { setActiveCode(e.target.value); resetForm(); }}>
                {attempted.map((e) => {
                  const ok = e.attempts.some((a) => a.state === "passed");
                  return (
                    <option key={e.exam_code} value={e.exam_code} disabled={!ok} style={ok ? undefined : { color: "#C0392B" }}>
                      {e.full_name} - {e.exam_code}{ok ? "" : " - Failed"}
                    </option>
                  );
                })}
              </select>
            </div>

            {showForm && (
              <>
                <div className="field full">
                  <label htmlFor="certFullName">Full name (to be printed on certificate) <Req /></label>
                  <input id="certFullName" type="text" maxLength={100} value={fullName} className={invalid.name ? "invalid" : ""}
                    required aria-required="true"
                    onChange={(e) => setFullName(e.target.value)} />
                </div>
                <div className="field">
                  <label>Member ID</label>
                  <input type="text" value={user?.member_id || ""} readOnly />
                </div>
                <div className="field">
                  <label htmlFor="certGovtIdType">Select government ID <Req /></label>
                  <select id="certGovtIdType" value={idType} className={invalid.type ? "invalid" : ""}
                    required aria-required="true"
                    onChange={(e) => { setIdType(e.target.value); setIdNumber(""); }}>
                    <option value="">Choose ID type…</option>
                    {Object.keys(ID_VALIDATORS).map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="certGovtIdNumber">Government ID number <Req /></label>
                  <input
                    id="certGovtIdNumber"
                    type="text"
                    required
                    aria-required="true"
                    autoComplete="off"
                    disabled={!rule}
                    maxLength={rule?.maxLength}
                    placeholder={rule ? rule.placeholder : "Select an ID type first"}
                    value={idNumber}
                    className={idValid === true ? "valid" : idValid === false || invalid.number ? "invalid" : ""}
                    onChange={(e) => setIdNumber(e.target.value)}
                  />
                  {rule && (
                    <div className={`field-hint${idValid === true ? " is-valid" : idValid === false ? " is-error" : ""}`}>
                      {idValid === true ? "✓ Looks valid." : idValid === false ? rule.error : rule.hint}
                    </div>
                  )}
                </div>
                <div className="field full">
                  <label>Upload government ID <Req /></label>
                  <label className={`file-drop${invalid.file ? " invalid" : ""}`} htmlFor="certGovtIdFile">
                    <div className="fi-icon">⬆</div>
                    <div className="fi-text">
                      {file ? <><b>{file.name}</b> selected — click to change</> : <><b>Click to upload</b> a clear photo or scan (PDF, JPG, PNG — max 5MB)</>}
                    </div>
                    <input ref={fileRef} id="certGovtIdFile" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onFile} required aria-required="true" />
                  </label>
                </div>
              </>
            )}
          </div>

          {showForm && (
            <>
              <div className="kyc-note">
                Your government ID is used only to verify your identity against your exam record and is reviewed by Hacktonomy Admin — it is never printed on the certificate. It is permanently deleted once your certificate is approved.
              </div>
              <div className={`cert-consent${invalid.consent ? " invalid" : ""}`}>
                <div className="cert-consent-title">Consent <Req /></div>
                <div className="cert-consent-text" tabIndex={0} aria-label="Consent text">
                  {exam.certificate_consent || "Consent text is not available. Please refresh the page."}
                </div>
                <label className="cert-consent-check" htmlFor="certConsent">
                  <input
                    id="certConsent"
                    type="checkbox"
                    checked={consent}
                    required
                    aria-required="true"
                    disabled={!exam.certificate_consent}
                    onChange={(e) => {
                      setConsent(e.target.checked);
                      setInvalid((s) => ({ ...s, consent: false }));
                    }}
                  />
                  <span>I have read and I agree to the consent above.</span>
                </label>
              </div>
              <div className="req-legend"><Req /> All fields are required.</div>
              {cert.status === "rejected" && (
                <div className="reject-note" style={{ marginTop: 0, marginBottom: 16 }}>
                  Your previous submission was not approved{cert.reject_reason ? `: ${cert.reject_reason}` : "."} Please correct the details and submit again.
                </div>
              )}
              {serverErr && <div className="alert error">{serverErr}</div>}
              <button type="button" className="btn btn-primary" disabled={submitting} onClick={submit}>
                {submitting ? "Submitting…" : "Verify credentials"}
              </button>
            </>
          )}

          {exam && cert.status === "pending" && (
            <div className="cert-pending show">
              <div className="p-icon">⏳</div>
              <h3>Submitted for verification</h3>
              <p>
                Your details and government ID have been sent to Hacktonomy Admin for review. You'll be notified here once your certificate is approved and ready to download — this usually takes 1–2 business days.
              </p>
            </div>
          )}

          {exam && cert.status === "revoked" && (
            <div className="reject-note">This certificate has been revoked{cert.reject_reason ? `: ${cert.reject_reason}` : "."} Please contact Hacktonomy Admin.</div>
          )}

          {exam && cert.status === "approved" && (
            <div className="cert-preview show">
              <div className="seal"><img src={Logo} alt="Hacktonomy" /></div>
              <h3>Certificate of Achievement</h3>
              <p>This certifies that <b>{cert.full_name || user?.name}</b> has successfully completed</p>
              <p><b>{exam.exam_code} - Certification</b></p>
              <p style={{ marginTop: -6, opacity: 0.8 }}>{exam.full_name}</p>
              <div className="cert-id">
                Certificate ID: {cert.certificate_id} · Issued {formatDate(cert.issued_at)}
              </div>
              <div className="cert-qr">
                <QRCode value={certificateVerifyUrl(cert.certificate_id)} type="svg" size={112} bordered={false} color="#0B0D12" bgColor="#FFFFFF" />
                <a href={certificateVerifyUrl(cert.certificate_id)} target="_blank" rel="noopener noreferrer">
                  Public check page
                </a>
              </div>
              <div className="cert-actions">
                <button type="button" className="btn btn-primary"
                  onClick={() => downloadCertificate({ user, exam, certificate: cert, logoSrc: Logo }).catch(() => message.error("Couldn't generate the PDF."))}>
                  Download certificate
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Certification;
